import React, { useState, useEffect, useCallback } from 'react';
import { fetchOptionChain } from '../api';

const REFRESH_INTERVAL = 15000;

export default function OptionChain() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [index, setIndex] = useState('Nifty');
  const [count, setCount] = useState(20);
  const [lastRefresh, setLastRefresh] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const res = await fetchOptionChain(index, count);
      if (res.data?.status === 'ok') setData(res.data);
    } catch {}
    setLoading(false);
    setLastRefresh(new Date().toLocaleTimeString());
  }, [index, count]);

  useEffect(() => {
    setLoading(true);
    loadData();
    const interval = setInterval(loadData, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [loadData]);

  const fmtNum = (v) => {
    if (!v && v !== 0) return '—';
    if (v >= 10000000) return (v / 10000000).toFixed(2) + 'Cr';
    if (v >= 100000) return (v / 100000).toFixed(2) + 'L';
    if (v >= 1000) return (v / 1000).toFixed(1) + 'K';
    return v.toLocaleString('en-IN');
  };

  const fmtPrice = (v) => (v > 0 ? v.toFixed(2) : '—');

  const fmtLots = (v) => {
    if (!v && v !== 0) return '—';
    return Number(v).toLocaleString('en-IN');
  };

  const chgClass = (v) => (v > 0 ? 'oc-positive' : v < 0 ? 'oc-negative' : '');

  const renderTable = (rows, label, type) => (
    <div className="oc-side-block">
      <h3 className={`oc-side-title ${type}`}>{label} — Top 5 by Volume</h3>
      {rows && rows.length > 0 ? (
        <div className="table-wrapper oc-table-wrap">
          <table className="oc-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Strike</th>
                <th>LTP</th>
                <th>Chg</th>
                <th>Chg%</th>
                <th>Open</th>
                <th>High</th>
                <th>Low</th>
                <th>Prev Close</th>
                <th>Volume (lots)</th>
                <th>OI (lots)</th>
                <th>OI Chg (lots)</th>
                <th>Avg Price</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.strike} className={r.strike === data?.atm_strike ? 'oc-atm-row' : ''}>
                  <td>{i + 1}</td>
                  <td className="oc-cell-strike">
                    <strong>{r.strike}</strong>
                    {r.strike === data?.atm_strike && <span className="oc-atm-label">ATM</span>}
                    <small className="oc-diff">{r.diff > 0 ? `+${r.diff}` : r.diff}</small>
                  </td>
                  <td className="oc-ltp"><strong>{fmtPrice(r.ltp)}</strong></td>
                  <td className={chgClass(r.netChange)}>{r.netChange > 0 ? '+' : ''}{fmtPrice(r.netChange)}</td>
                  <td className={chgClass(r.percentChange)}>{r.percentChange > 0 ? '+' : ''}{r.percentChange?.toFixed(2) || '—'}%</td>
                  <td>{fmtPrice(r.open)}</td>
                  <td>{fmtPrice(r.high)}</td>
                  <td>{fmtPrice(r.low)}</td>
                  <td>{fmtPrice(r.close)}</td>
                  <td className="oc-top-vol"><strong>{fmtLots(r.volume)}</strong></td>
                  <td><strong>{fmtLots(r.oi)}</strong></td>
                  <td className={chgClass(r.oiChange)}>{r.oiChange > 0 ? '+' : ''}{fmtLots(r.oiChange)}</td>
                  <td>{fmtPrice(r.avgPrice)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="dash-empty">No data</div>
      )}
    </div>
  );

  return (
    <div className="page-option-chain">
      <div className="dash-page-header">
        <div>
          <h1 className="page-title">Option Chain</h1>
          <p className="page-subtitle">
            {data ? `${data.index} — Spot: ${data.spot_price?.toLocaleString('en-IN', { minimumFractionDigits: 2 })} | ATM: ${data.atm_strike} | Expiry: ${data.expiry} | Lot: ${data.lot_size}` : 'Loading...'}
          </p>
        </div>
        {lastRefresh && (
          <span className="live-indicator">
            <span className="live-pulse" /> Updated {lastRefresh}
          </span>
        )}
      </div>

      {/* Controls */}
      <div className="inst-filters">
        <select value={index} onChange={e => setIndex(e.target.value)}>
          <option value="Nifty">Nifty</option>
          <option value="Sensex">Sensex</option>
        </select>
        <select value={count} onChange={e => setCount(Number(e.target.value))}>
          <option value={10}>±10 Strikes</option>
          <option value={15}>±15 Strikes</option>
          <option value={20}>±20 Strikes</option>
          <option value={30}>±30 Strikes</option>
        </select>
      </div>

      {loading && !data ? (
        <div className="loading"><div className="spinner" /><p>Loading option chain...</p></div>
      ) : data ? (
        <>
          {renderTable(data.ce_top5, 'CALLS (CE)', 'ce')}
          {renderTable(data.pe_top5, 'PUTS (PE)', 'pe')}
        </>
      ) : (
        <div className="dash-empty">No option chain data available</div>
      )}
    </div>
  );
}
